import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract (see the framing addendum). 'forbidden': there is no 3D
// scene here at all - one screen-filling quad and a fragment shader. A light
// rig would have nothing to light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'kaleidodrift',
  family: 'shader',
  energyLevel: 0.60,
  bpmMin: 85,
  bpmMax: 150,
  movement: 0.70,
  danceability: 0.70,
  tonality: 0.60,
  enabled: true
};

// KaleidoDrift: a full-frame kaleidoscope whose SOURCE IMAGE is the album
// cover. The classic VJ idiom - Resolume / VDMX / Boiler Room projections:
// radial symmetry, domain repetition, slow rotation, endless zoom.
//
// The cover is the pattern, which is the whole point: every track produces a
// different kaleidoscope with no extra data whatsoever. This is the only
// visualizer that uses the artwork as a TEXTURE rather than displaying it.
//
// PERFORMANCE SHAPE - this one is different from every other visualizer here.
// There is no geometry to speak of (two triangles) and no draw-call pressure;
// it is 100% fill-rate, every pixel running the same fragment shader. The
// budget is therefore expressed per pixel: TWO texture reads and roughly 45
// ALU ops. No loop, no third layer, no procedural noise, mediump throughout.
// atan() is computed ONCE and shared by both layers - it is the single most
// expensive op in here and doing it twice would blow the budget on its own.

const CAMERA_POS = [0, 2, 8];
const CAMERA_LOOK = [0, 2, 0];

// Sector counts are drawn from this set on a phrase downbeat. All even, so the
// mirror fold always lands cleanly on the horizontal axis.
const SEGMENT_CHOICES = [6, 8, 12, 16];
const PHRASE_SECONDS = 14; // fallback pacing when the track has no BPM

// One full SECTOR of rotation every 16 beats - tempo-locked, but continuous:
// a kaleidoscope that stepped on the beat would strobe, which is exactly what
// the brief rules out for this pattern.
const BEATS_PER_SECTOR = 16;
const REFERENCE_BPM = 120;

const ZOOM_MIN = 0.7;  // wide, at the very start/end of a track
const ZOOM_MAX = 2.2;  // dense, through the middle
const ZOOM_PULSE_AMOUNT = 0.18;
const ZOOM_PULSE_DECAY = 10;
const SCROLL_SPEED = 0.035;

const SATURATION_BOOST = 1.7;
const THRESHOLD_LO = 0.12;
const THRESHOLD_HI = 0.85;
const VIGNETTE_IN = 0.62;
const VIGNETTE_OUT = 1.05;
const VIGNETTE_DEPTH = 0.55;
const LAYER2_MIX = 0.55;
const TINT_CYCLE_SPEED = 0.06;

const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f];
const FALLBACK_TEXTURE_SIZE = 64;

const TIER_LAYOUT = {
  // The two knobs that actually cost per-pixel work: the second layer is a
  // whole extra texture read plus its own fold, and the soft threshold is
  // three smoothsteps.
  0: { layers: 1, threshold: false, saturate: true },
  1: { layers: 2, threshold: true, saturate: false },
  2: { layers: 2, threshold: true, saturate: true }
};
const DEFAULT_TIER = 1;


// The quad is emitted straight in clip space, ignoring the camera entirely:
// position.xy spans [-0.5, 0.5] on a unit plane, so doubling it covers exactly
// NDC [-1, 1]. That also makes the effect immune to SceneManager's Ken Burns
// camera drift, which would otherwise slide a full-frame pattern around.
const vertexShader = `
  precision mediump float;

  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function buildFragmentShader(layout) {
  // The fold is what makes this a kaleidoscope. mod() alone repeats the domain
  // rotationally - the pattern spins but every sector is a straight copy, with
  // a hard seam at each boundary. The abs() reflects the second half of each
  // sector back onto the first, so adjacent sectors are mirror images and the
  // seams disappear.
  const kaleidoChunk = `
  vec3 kaleido(float angle, float r, float segments, float zoom, float scroll) {
    float seg = 6.28318530718 / segments;
    float a = mod(angle, seg);
    a = abs(a - seg * 0.5);
    vec2 q = vec2(cos(a), sin(a)) * r;
    q = q * zoom + vec2(scroll, scroll * 0.6);
    // The source texture is RepeatWrapping, so fract() here is belt and
    // braces - without the wrap mode it would clamp and streak at the edges.
    vec2 uv = fract(q * 0.5 + 0.5);
    return texture2D(uArtwork, uv).rgb;
  }
  `;

  // Tint is a function of the FOLDED angle only. That matters: anything keyed
  // to the raw sector index would differ between neighbouring sectors and
  // break the mirror symmetry the whole effect rests on. Keyed to the fold, it
  // sweeps identically across every sector and stays symmetric about each
  // sector's own axis.
  const tintChunk = `
  vec3 paletteTint(float foldedAngle, float seg) {
    float ti = mod(foldedAngle / seg * 4.0 + uTintPhase, 3.0);
    if (ti < 1.0) return mix(uVivid0, uVivid1, ti);
    if (ti < 2.0) return mix(uVivid1, uVivid2, ti - 1.0);
    return mix(uVivid2, uVivid0, ti - 2.0);
  }
  `;

  const secondLayer = layout.layers > 1 ? `
    // Counter-rotating, twice the zoom. The opposing direction is what reads
    // as depth: two layers turning the same way just look like one thicker
    // layer. Screen blend so the bright parts of either survive.
    vec3 col2 = kaleido(-baseAngle, r, uSegments2, uZoom * 2.0, -uScroll * 0.8);
    col = 1.0 - (1.0 - col) * (1.0 - col2 * ${LAYER2_MIX.toFixed(2)});
  ` : '';

  const saturate = layout.saturate ? `
    // Cheap saturation, no HSL round trip: push away from the luminance grey.
    // A kaleidoscoped cover without this comes out brown and muddy, because
    // averaging many samples of any photograph tends toward mud.
    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(lum), col, ${SATURATION_BOOST.toFixed(2)});
  ` : '';

  const threshold = layout.threshold ? `
    // Crushes the midtones and keeps the vivid zones - the difference between
    // "a photo folded up" and a graphic pattern.
    col = smoothstep(${THRESHOLD_LO.toFixed(2)}, ${THRESHOLD_HI.toFixed(2)}, col);
  ` : '';

  return `
  precision mediump float;

  uniform sampler2D uArtwork;
  uniform float uAspect;
  uniform float uRotation;
  uniform float uSegments;
  uniform float uSegments2;
  uniform float uZoom;
  uniform float uScroll;
  uniform float uTintPhase;
  uniform vec3 uVivid0;
  uniform vec3 uVivid1;
  uniform vec3 uVivid2;

  varying vec2 vUv;

  ${kaleidoChunk}
  ${tintChunk}

  void main() {
    vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
    float r = length(p);
    // ONE atan for the whole shader - both layers reuse it.
    float baseAngle = atan(p.y, p.x) + uRotation;

    vec3 col = kaleido(baseAngle, r, uSegments, uZoom, uScroll);
    ${secondLayer}
    ${saturate}
    ${threshold}

    float seg = 6.28318530718 / uSegments;
    float folded = abs(mod(baseAngle, seg) - seg * 0.5);
    col *= paletteTint(folded, seg);

    // Without this the corners saturate and the frame flattens into a slab.
    col *= 1.0 - smoothstep(${VIGNETTE_IN.toFixed(2)}, ${VIGNETTE_OUT.toFixed(2)}, r) * ${VIGNETTE_DEPTH.toFixed(2)};

    gl_FragColor = vec4(col, 1.0);
  }
  `;
}

// Three crossed sines, baked ONCE into a small texture rather than branched
// for in the fragment shader. That keeps the shader to a single code path at
// exactly the budgeted cost, and the pattern is only ever needed when there is
// no cover at all - in which case there is no palette either, so nothing about
// it needs to change at runtime. Every multiplier is a whole number of cycles
// across the texture, so it tiles seamlessly under RepeatWrapping.
function buildFallbackTexture() {
  const N = FALLBACK_TEXTURE_SIZE;
  const data = new Uint8Array(N * N * 4);
  const c = FALLBACK_COLORS.map((hex) => new THREE.Color(hex));
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const u = (x / N) * Math.PI * 2;
      const v = (y / N) * Math.PI * 2;
      const s0 = 0.5 + 0.5 * Math.sin(u * 3);
      const s1 = 0.5 + 0.5 * Math.sin(v * 2 + 1.7);
      const s2 = 0.5 + 0.5 * Math.sin((u + v) * 2 + 0.9);
      const i = (y * N + x) * 4;
      data[i] = Math.round(255 * Math.min(1, c[0].r * s0 + c[1].r * s1 + c[2].r * s2));
      data[i + 1] = Math.round(255 * Math.min(1, c[0].g * s0 + c[1].g * s1 + c[2].g * s2));
      data[i + 2] = Math.round(255 * Math.min(1, c[0].b * s0 + c[1].b * s1 + c[2].b * s2));
      data[i + 3] = 255;
    }
  }
  const texture = new THREE.DataTexture(data, N, N);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

export class KaleidoDriftVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    // The quad ignores the camera, but other things (the DOM overlay's own
    // framing assumptions) do not, so leave it somewhere sane.
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    this._fallbackTexture = buildFallbackTexture();

    this._uniforms = {
      uArtwork: { value: this._fallbackTexture },
      uAspect: { value: 16 / 9 },
      uRotation: { value: 0 },
      uSegments: { value: SEGMENT_CHOICES[1] },
      uSegments2: { value: SEGMENT_CHOICES[2] },
      uZoom: { value: ZOOM_MIN },
      uScroll: { value: 0 },
      uTintPhase: { value: 0 },
      uVivid0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uVivid1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uVivid2: { value: new THREE.Color(FALLBACK_COLORS[2]) }
    };

    const layout = TIER_LAYOUT[this._tier];
    const geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader(layout),
      // Opaque and depth-free: it is the only thing in the scene and it covers
      // every pixel, so there is nothing to blend with or sort against.
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._quad = new THREE.Mesh(geometry, this._material);
    this._quadGeometry = geometry;
    // Clip-space output makes the mesh's own bounds meaningless to the culler.
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -1000;
    scene.add(this._quad);

    this._time = 0;
    this._rotation = 0;
    // CHANTIER 6 (revision) - segment changes now fire on
    // director.accentPhrase's own rising edge (once per 16-beat phrase)
    // instead of this file's own local beat counter - see update()'s own
    // edge detection. _phraseEndsAtTime is the no-bpm fallback only.
    this._lastAccentPhrase = null;
    this._phraseEndsAtTime = PHRASE_SECONDS;
    this._lastArtworkUrl = null;
    this._lastPaletteRef = null;
    this._hasArtwork = false;
    this._artworkTexture = null;
  }

  _loadArtwork(url) {
    // RepeatWrapping is not optional here: the shader's fract() tiles the
    // source, and with the default ClampToEdge every tile boundary becomes a
    // smeared streak. See artwork-texture.js - halftone.js uses the same
    // loader with ClampToEdge instead, for exactly the opposite reason.
    loadArtworkTexture(
      url,
      { wrap: THREE.RepeatWrapping, mipmaps: true },
      (texture) => {
        const previous = this._artworkTexture;
        this._artworkTexture = texture;
        this._uniforms.uArtwork.value = texture;
        this._hasArtwork = true;
        if (previous) previous.dispose();
      },
      undefined,
      () => {
        // A cover that will not load falls back to the baked pattern rather
        // than to black - this visualizer must never render an empty frame.
        this._hasArtwork = false;
        this._uniforms.uArtwork.value = this._fallbackTexture;
      }
    );
  }

  _switchSegments() {
    const current = this._uniforms.uSegments.value;
    const others = SEGMENT_CHOICES.filter((s) => s !== current);
    const next = others[Math.floor(Math.random() * others.length)];
    this._uniforms.uSegments.value = next;
    // The second layer always differs from the first, so the two folds never
    // line up and cancel into a single apparent layer.
    const seconds = SEGMENT_CHOICES.filter((s) => s !== next);
    this._uniforms.uSegments2.value = seconds[Math.floor(Math.random() * seconds.length)];
    this._phraseEndsAtTime = this._time + PHRASE_SECONDS;
  }

  _applyPalette(profile) {
    const palette = profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const slots = [this._uniforms.uVivid0.value, this._uniforms.uVivid1.value, this._uniforms.uVivid2.value];
    for (let i = 0; i < 3; i++) {
      slots[i].set(vivid.length > 0 ? vivid[i % vivid.length] : FALLBACK_COLORS[i]);
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    // Freezing time freezes rotation, scroll and tint together - the whole
    // pattern stops dead rather than drifting on without its beat.
    if (!paused) this._time += deltaTime;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || REFERENCE_BPM;
    const segments = this._uniforms.uSegments.value;
    if (!paused) {
      // One full SECTOR every BEATS_PER_SECTOR beats, so the rotation is
      // locked to the music at any tempo.
      const sectorRadians = (Math.PI * 2) / segments;
      const beatsPerSecond = bpm / 60;
      this._rotation += deltaTime * sectorRadians * beatsPerSecond / BEATS_PER_SECTOR;
      if (this._rotation > Math.PI * 2) this._rotation -= Math.PI * 2;
    }
    this._uniforms.uRotation.value = this._rotation;
    this._uniforms.uScroll.value = this._time * SCROLL_SPEED;
    this._uniforms.uTintPhase.value = this._time * TINT_CYCLE_SPEED;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      this._hasArtwork = false;
      this._uniforms.uArtwork.value = this._fallbackTexture;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }

    // Segment count changes only on a phrase downbeat - a sector count that
    // jumped mid-bar would be plainly visible as a snap. CHANTIER 6
    // (revision): driven by director.accentPhrase's own rising edge now,
    // not a locally-rolled beat counter - see director.js.
    if (!paused) {
      const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
      const accentPhrase = profile.director ? profile.director.accentPhrase : null;
      const accentRisingEdge = accentPhrase === 1 && this._lastAccentPhrase !== 1;
      this._lastAccentPhrase = accentPhrase;
      const dueOnAccent = bpm && accentRisingEdge;
      const dueOnClock = !bpm && this._time >= this._phraseEndsAtTime;
      if (dueOnAccent || dueOnClock) this._switchSegments();
    }

    // The pattern BREATHES on the beat rather than flashing: a full-frame
    // brightness strobe on a kaleidoscope is unwatchable.
    const pulse = profile.director.pulse(ZOOM_PULSE_AMOUNT, ZOOM_PULSE_DECAY);
    const intensity = profile.director.intensity;
    this._uniforms.uZoom.value = (ZOOM_MIN + (ZOOM_MAX - ZOOM_MIN) * intensity) * pulse;

    this._uniforms.uAspect.value = this._camera.aspect;
  }

  dispose() {
    this._scene.remove(this._quad);
    this._quadGeometry.dispose();
    this._material.dispose();
    this._fallbackTexture.dispose();
    if (this._artworkTexture) {
      this._artworkTexture.dispose();
      this._artworkTexture = null;
    }
  }
}
